import React, { useEffect, useState } from 'react';
import { reportAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reportAPI.getDashboard()
      .then(res => setStats(res.data.data))
      .catch(() => toast.error('Failed to load report data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <span className="spinner" style={{ width: 32, height: 32 }} />
    </div>
  );

  const cartonBreakdown = [
    { name: 'Pending Sort', value: stats?.pendingSorting || 0, fill: '#f59e0b' },
    { name: 'In Transit', value: stats?.inTransit || 0, fill: '#8b5cf6' },
    { name: 'Arrived BD', value: stats?.arrivedBD || 0, fill: '#06b6d4' },
    { name: 'Delivered', value: stats?.deliveredCartons || 0, fill: '#10b981' },
  ];

  const shipmentBreakdown = [
    { name: 'Active', value: stats?.activeShipments || 0, fill: '#3b82f6' },
    { name: 'Completed', value: stats?.completedShipments || 0, fill: '#10b981' },
  ];

  const StatRow = ({ label, value, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: color || 'var(--text-primary)' }}>{value ?? '—'}</span>
    </div>
  );

  return (
    <div className="animate-fade">
      <div className="page-header">
        <h1 className="page-title">Reports</h1>
        <p className="page-subtitle">Business overview and performance metrics</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Carton summary */}
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, marginBottom: 16, color: 'var(--text-secondary)' }}>Carton Summary</h3>
          <StatRow label="Total Cartons" value={stats?.totalCartons} color="var(--accent)" />
          <StatRow label="Pending Sorting" value={stats?.pendingSorting} color="var(--warning)" />
          <StatRow label="In Transit" value={stats?.inTransit} color="#8b5cf6" />
          <StatRow label="Arrived BD" value={stats?.arrivedBD} color="var(--accent-2)" />
          <StatRow label="Delivered" value={stats?.deliveredCartons} color="var(--success)" />
        </div>

        {/* Shipment summary */}
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, marginBottom: 16, color: 'var(--text-secondary)' }}>Shipment Summary</h3>
          <StatRow label="Total Shipments" value={stats?.totalShipments} color="var(--accent)" />
          <StatRow label="Active" value={stats?.activeShipments} color="var(--warning)" />
          <StatRow label="Completed" value={stats?.completedShipments} color="var(--success)" />
        </div>

        {/* Performance */}
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, marginBottom: 16, color: 'var(--text-secondary)' }}>Performance</h3>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, fontFamily: 'var(--font-display)', fontWeight: 800, color: 'var(--success)' }}>
              {stats?.totalCartons ? Math.round(((stats.deliveredCartons || 0) / stats.totalCartons) * 100) : 0}%
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>Delivery Rate</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)' }}>
              {stats?.totalShipments ? Math.round(((stats.completedShipments || 0) / stats.totalShipments) * 100) : 0}%
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Shipment Completion</div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, marginBottom: 20 }}>Carton Status Breakdown</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={cartonBreakdown} barSize={36}>
              <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
              <Bar dataKey="value" radius={[4,4,0,0]}>
                {cartonBreakdown.map((entry, i) => (
                  <rect key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, marginBottom: 20 }}>Shipment Status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={shipmentBreakdown} barSize={60}>
              <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
              <Bar dataKey="value" fill="#3b82f6" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
